import { useDeleteSupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import useI18n from '@/shared/hooks/usei18n'
import { Alert, Button, GenericDialog } from '@/common'
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'

export default function DeleteSupplierEmissionsReportDialog({
  isOpen,
  close,
  reportName,
  partnershipId,
  installationId,
  supplierEmissionsReportId,
  onAfterLeave
}: {
  isOpen: boolean
  close: () => void
  reportName: string
  partnershipId: string
  installationId: string
  supplierEmissionsReportId: string
  onAfterLeave?: () => void
}) {
  const { t } = useI18n()
  const { mutateAsync: deleteSupplierEmissionsReport, isPending: isDeleting } =
    useDeleteSupplierEmissionsReport(partnershipId, installationId, supplierEmissionsReportId)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onDeleteClick = useCallback(async () => {
    try {
      setErrorMessage(null)
      await deleteSupplierEmissionsReport()
      toast.success(
        t(
          'supplierEmissionsReport:emissionReports.dialogs.deleteSupplierEmissionsReport.notifications.success'
        )
      )
      close()
      if (onAfterLeave) {
        onAfterLeave()
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.message

      if (errorCode === 'SUPPLIER_EMISSIONS_REPORT_HAS_IMPORTED_GOODS') {
        setErrorMessage(
          t(
            'supplierEmissionsReport:emissionReports.dialogs.deleteSupplierEmissionsReport.errors.supplierEmissionsReportHasImportedGoods'
          )
        )
      } else {
        toast.error(
          t(
            'supplierEmissionsReport:emissionReports.dialogs.deleteSupplierEmissionsReport.notifications.error'
          )
        )
      }
    }
  }, [deleteSupplierEmissionsReport, close, t, onAfterLeave])

  const onClose = useCallback(() => {
    close()
    setErrorMessage(null)
  }, [close])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('supplierEmissionsReport:emissionReports.dialogs.deleteSupplierEmissionsReport.title')}
      size="md"
    >
      <div className="space-y-4">
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <p>
          <Trans
            i18nKey="supplierEmissionsReport:emissionReports.dialogs.deleteSupplierEmissionsReport.description"
            values={{ name: reportName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="danger" onClick={onDeleteClick} isLoading={isDeleting}>
            {t('common:actions.delete')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
